import React, { useCallback, useMemo, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'ncoded-component-library';
import { MessageType, SessionType } from 'models/Message';
import { useNavigate } from 'react-router-dom';
import { SessionCardPlaceholder } from './placeholders';
import EmptyScreen from 'components/EmptyScreen';
import PencilaltIcon from 'icons/Pencilalt.icon';
import SearchIcon from 'icons/Search.icon';
import SessionCard from './components/SessionCard';
import InfiniteScroll from 'components/InfiniteScroll';
import useChatListener from '../../pages/Chat/hooks/useChatListeners';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useInfinitePagination from 'hooks/useInfinitePagination';
import useEffectOnceWhen from 'hooks/useEffectOnce';
import useMatchMedia from 'hooks/useMatchMedia';
import { CallbackBusType, EventName } from 'modules/bus';
import uniqBy from 'lodash/uniqBy';
import api from 'api';
import { getHasElementsInArray, getIsArrayEmpty } from 'utils';
import useMessagingNavigation from '../../hooks/useMessagingNavigation';
import { MessageCreatedEventData } from 'services/socketService/socketEventData';
import useMarkSessionRead from '../../hooks/useMarkSessionRead';

import './MessagingContacts.styles.scss';

const NUMBER_OF_PLACEHOLDERS = {
  INITIAL_LOADING_COUNT: 6,
  PAGINATED_LOADING_COUNT: 2,
} as const;

type MessagingContactsProps = {
  className?: string;
  onNewMessage: () => void;
};

const MessagingContacts: React.FC<MessagingContactsProps> = (props) => {
  const { className, onNewMessage } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { markSessionUnread } = useContext(CurrentUserContext);
  const {
    sessionId,
    currentSession,
    messagingPortfolioProps,
    setContactsOpen,
  } = useContext(MessagingContext);

  const classes = classNames('messaging-contacts', className);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    loading,
    currentPage,
    searchString,
    items: sessions,
    setSearchString,
    setItems: setSessions,
    onContainerScrolled: onSessionScroll,
  } = useInfinitePagination<SessionType>({
    makeRequest: async (currentPage: number, searchString: string) => {
      return api.messages
        .getSessions({
          $page: currentPage.toString(),
          $limit: '10',
          $sort: '-latestMessageAt',
          $searchString: searchString,
          openSessionId: sessionId,
          listingId: messagingPortfolioProps?.listingId,
        })
        .then((res) => res.data);
    },
  });

  const getNavigationLink = useMessagingNavigation();

  const finalSessions = useMemo(
    () => sessions && uniqBy(sessions, '_id'),
    [sessions],
  );

  const numOfPlaceholders = useMemo(
    () =>
      currentPage > 1
        ? NUMBER_OF_PLACEHOLDERS.PAGINATED_LOADING_COUNT
        : NUMBER_OF_PLACEHOLDERS.INITIAL_LOADING_COUNT,
    [currentPage],
  );

  const emptyPlaceholder = useMemo(
    () => (
      <div className="messaging-contacts__placeholder">
        {loading &&
          Array(numOfPlaceholders)
            .fill(1)
            .map((_, i) => <SessionCardPlaceholder key={i} />)}
      </div>
    ),
    [loading, numOfPlaceholders],
  );

  const updateSession = useCallback(
    (sessionId: string, updatedSessionValues: Partial<SessionType>) =>
      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          sessionId !== session._id
            ? session
            : { ...session, ...updatedSessionValues },
        ),
      ),
    [setSessions],
  );

  const markSessionAsUnread = useCallback(
    async (sid: string) => {
      try {
        await api.messages.unreadAllMessages(sid);

        markSessionUnread(sid);

        updateSession(sid, { hasUnreadMessages: true });
      } catch (e) {
        popServerError(e);
      }
    },
    [popServerError, updateSession, markSessionUnread],
  );

  const markSessionRead = useMarkSessionRead();

  const markSessionAsRead = useCallback(
    (sid: string, isExternalCommunication: boolean) => {
      // optimistic: drop the card state before the API resolves
      updateSession(sid, { hasUnreadMessages: false });
      markSessionRead(sid, isExternalCommunication);
    },
    [updateSession, markSessionRead],
  );

  const onSessionPicked = useCallback(
    (data: CallbackBusType<SessionType>) => {
      const { payload: session } = data;

      navigate(getNavigationLink(session._id), {
        replace: true,
      });

      setSessions((oldSessions) => uniqBy([session, ...oldSessions], '_id'));
    },
    [navigate, getNavigationLink, setSessions],
  );

  const onMessageArrived = useCallback(
    ({ message, session }: MessageCreatedEventData) => {
      const { sessionId: msgSessionId } = message;

      // provider adds the unread session id globally on message-created;
      // if it's the open chat, read it right away (optimistically drops the id)
      if (sessionId === msgSessionId)
        markSessionAsRead(msgSessionId, session.isExternalCommunication);

      setSessions((oldSessions) => [
        { ...session, hasUnreadMessages: sessionId !== msgSessionId },
        ...oldSessions.filter((session) => session._id !== msgSessionId),
      ]);
    },
    [sessionId, markSessionAsRead, setSessions],
  );

  const messageSentHandler = useCallback(
    async (data: { event: EventName; payload: MessageType }) => {
      const { sessionId } = data.payload;

      setSessions((oldSessions) => {
        const session = oldSessions.find((s) => s._id === sessionId);

        if (!session)
          return [
            {
              ...currentSession,
              latestMessageAt: new Date(),
            },
            ...oldSessions,
          ];

        // read all messages from session only if there are unread messages
        if (session.hasUnreadMessages)
          markSessionAsRead(sessionId, session.isExternalCommunication);

        return oldSessions
          .map((session) =>
            session._id === sessionId
              ? {
                  ...session,
                  latestMessageAt: new Date(),
                }
              : session,
          )
          .sort((first, second) => {
            const firstDate = new Date(first.latestMessageAt).getTime();
            const secondDate = new Date(second.latestMessageAt).getTime();

            return firstDate > secondDate ? -1 : 1;
          });
      });
    },
    [currentSession, markSessionAsRead, setSessions],
  );

  useChatListener({
    onMessageArrived,
    onMessageSent: messageSentHandler,
    onSessionPicked,
  });

  useEffectOnceWhen(() => {
    if (sessionId) return;

    navigate(getNavigationLink(sessions[0]._id), { replace: true });
  }, getHasElementsInArray(sessions));

  useEffect(() => {
    if (!sessionId || sessions?.some(({ _id }) => _id === sessionId)) return;

    (async () => {
      try {
        const { data } = await api.messages.getSessionById(sessionId);
        setSessions((prev) => [data, ...prev]);
      } catch (e) {
        popServerError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, popServerError, setSessions]);

  useEffect(() => {
    const { _id, isExternalCommunication, hasUnreadMessages } =
      currentSession || {};
    if (hasUnreadMessages && sessionId === _id) {
      markSessionAsRead(sessionId, isExternalCommunication);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, sessionId]);

  return (
    <div className={classes}>
      <div className="messaging-contacts__header">
        <div className="messaging-contacts__actions">
          <p className="messaging-contacts__actions__title">{t('messaging')}</p>
          <Button
            className="contact"
            variant="outline"
            icon={PencilaltIcon}
            onClick={onNewMessage}
          >
            {t('composeMessage')}
          </Button>
        </div>

        <Input
          className="messaging-contacts__search"
          value={searchString}
          prefixNode={<SearchIcon />}
          placeholder={t('searchListingOrDealName')}
          onChange={(e) => setSearchString(e.target.value)}
        />
      </div>

      <InfiniteScroll
        localScroll
        className="messaging-contacts__sessions"
        onScroll={onSessionScroll}
      >
        {!loading && getIsArrayEmpty(sessions) ? (
          <EmptyScreen
            className="messaging-contacts__empty-screen"
            title={t(
              searchString
                ? 'thereAreNoContactsForSearchedUser'
                : 'youHaveNoContactsRightNow',
            )}
            {...(!searchString && {
              content: t('participantsForListingAreGoingToBecamaAvailable'),
            })}
          />
        ) : (
          finalSessions?.map((session) => (
            <SessionCard
              key={session._id}
              session={session}
              markSessionAsUnread={markSessionAsUnread}
              onClick={() => {
                if (isPhablet) setContactsOpen(false);
              }}
            />
          ))
        )}

        {emptyPlaceholder}
      </InfiniteScroll>
    </div>
  );
};

export default MessagingContacts;
